/**
 * /clinician/credentialing — deep-dive credentialing management page.
 *
 * Backs the "we manage your credentialing end-to-end" pitch claim by
 * giving the clinician one screen to:
 *   - see KPIs (approved / in-progress / expiring <90d)
 *   - open a new credentialing case for a payer
 *   - expand any active case to toggle the 8-item document checklist
 *   - attest CAQH and link a CAQH ProView ID
 *   - submit the packet to the payer (validates required items; shows the
 *     missing list explicitly if incomplete)
 *   - manage expirable credentials (state license, DEA, malpractice,
 *     board cert) with explicit 90-day re-cred alerts
 *
 * Reads / writes:
 *   GET  /api/clinician/credentialing
 *   POST /api/clinician/credentialing/start
 *   PUT  /api/clinician/credentialing/{id}/checklist
 *   POST /api/clinician/credentialing/{id}/caqh
 *   POST /api/clinician/credentialing/{id}/submit
 *   GET  /api/clinician/credentialing/expirables
 *   POST /api/clinician/credentialing/expirables
 *
 * Honest framing:
 *   - No "ready to bill" pill until a payer actually approves the case.
 *   - The CAQH and payer-submit calls are vendor adapters today; we say
 *     "Submitted — payer review takes 60–120 days" rather than implying
 *     instant approval.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from '../lib/api';
import {
  Plus, ShieldCheck, AlertTriangle, CheckCircle2, Circle, Clock, ChevronRight,
  Building2, Calendar, FileText, ExternalLink, Loader2, ArrowRight,
} from 'lucide-react';
import { DashboardShell } from '../components/brand/DashboardShell';
import { clinicianNavItems } from '../components/brand/navItems';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_META = {
  intake:            { label: 'Intake',            pct: 10, tone: 'cn-primary' },
  data_collection:   { label: 'Gathering docs',    pct: 30, tone: 'cn-primary' },
  caqh_attested:     { label: 'CAQH attested',     pct: 55, tone: 'cn-primary' },
  submitted_to_payer:{ label: 'Submitted',         pct: 75, tone: 'cn-primary' },
  payer_review:      { label: 'Payer review',      pct: 90, tone: 'cn-accent' },
  approved:          { label: 'Approved',          pct: 100, tone: 'cn-success' },
  denied:            { label: 'Denied',            pct: 100, tone: 'cn-accent' },
};

const EXPIRABLE_LABEL = {
  state_license: 'State license',
  dea: 'DEA registration',
  malpractice: 'Malpractice / liability COI',
  board_cert: 'Board certification',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return iso; }
};

const StagePill = ({ stage }) => {
  const meta = STAGE_META[stage] || { label: stage, tone: 'cn-text-muted' };
  return (
    <span
      className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-${meta.tone}/10 text-${meta.tone}`}
      data-testid={`stage-pill-${stage}`}
    >
      {meta.label}
    </span>
  );
};

const ProgressBar = ({ stage }) => {
  const meta = STAGE_META[stage] || { pct: 0, tone: 'cn-primary' };
  return (
    <div className="w-full h-2 rounded-full bg-cn-bg/80 overflow-hidden">
      <div
        className={`h-full bg-${meta.tone} transition-all`}
        style={{ width: `${meta.pct}%` }}
      />
    </div>
  );
};


// ───────────────────────────────────────────── Start-new-case form ─────────
const StartCaseForm = ({ onCreated, onCancel }) => {
  const { getAuthHeader } = useAuth();
  const [payerName, setPayerName] = useState('');
  const [payerId, setPayerId] = useState('');
  const [network, setNetwork] = useState('');
  const [caqhId, setCaqhId] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!payerName.trim() || !payerId.trim()) {
      toast.error('Payer name and ID are required');
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/clinician/credentialing/start`,
        { payer_name: payerName.trim(), payer_id: payerId.trim(),
          network: network.trim() || null, caqh_id: caqhId.trim() || null },
        getAuthHeader());
      toast.success(`Credentialing case opened for ${payerName.trim()}`);
      onCreated?.(r.data.case);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not open case');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="cn-card p-5 border border-cn-primary/30 bg-cn-primary/5"
          data-testid="start-credentialing-form">
      <h3 className="font-display font-bold text-cn-text dark:text-white mb-3">Open a new credentialing case</h3>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">Payer name *</label>
          <input
            value={payerName}
            onChange={(e) => setPayerName(e.target.value)}
            placeholder="e.g. Aetna"
            className="cn-input w-full mt-1"
            data-testid="start-case-payer-name"
            required
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">Payer ID *</label>
          <input
            value={payerId}
            onChange={(e) => setPayerId(e.target.value)}
            placeholder="e.g. aetna-60054"
            className="cn-input w-full mt-1"
            data-testid="start-case-payer-id"
            required
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">Network</label>
          <input
            value={network}
            onChange={(e) => setNetwork(e.target.value)}
            placeholder="e.g. Commercial PPO"
            className="cn-input w-full mt-1"
            data-testid="start-case-network"
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">CAQH ProView ID</label>
          <input
            value={caqhId}
            onChange={(e) => setCaqhId(e.target.value)}
            placeholder="Optional — can be added later"
            className="cn-input w-full mt-1"
            data-testid="start-case-caqh-id"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <Button type="button" onClick={onCancel} className="cn-btn cn-btn-ghost !py-2 !px-3 !text-sm"
                data-testid="start-case-cancel">
          Cancel
        </Button>
        <Button type="submit" disabled={busy} className="cn-btn cn-btn-primary !py-2 !px-3 !text-sm"
                data-testid="start-case-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Open case'}
        </Button>
      </div>
    </form>
  );
};


// ───────────────────────────────────────────── Single case card ───────────
const CaseCard = ({ caseDoc, onChanged }) => {
  const { getAuthHeader } = useAuth();
  const [open, setOpen] = useState(false);
  const [caqhId, setCaqhId] = useState(caseDoc.caqh_id || '');
  const [submitting, setSubmitting] = useState(false);
  const [missing, setMissing] = useState(null);

  const toggleItem = async (item) => {
    try {
      const r = await axios.put(`${API}/clinician/credentialing/${caseDoc.id}/checklist`,
        { key: item.key, done: !item.done }, getAuthHeader());
      onChanged?.({ ...caseDoc, checklist: r.data.checklist, stage: r.data.stage });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };

  const attestCaqh = async () => {
    if (!caqhId.trim()) { toast.error('Enter your CAQH ProView ID'); return; }
    try {
      const r = await axios.post(`${API}/clinician/credentialing/${caseDoc.id}/caqh`,
        { caqh_id: caqhId.trim(), attested: true }, getAuthHeader());
      if (r.data.attested) {
        toast.success('CAQH attested — checklist updated');
      } else {
        toast.message('CAQH linked — attestation still pending', { description: 'Complete the ProView attestation, then retry.' });
      }
      onChanged?.(); // refresh full case
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'CAQH attestation failed');
    }
  };

  const submitToPayer = async () => {
    setSubmitting(true);
    setMissing(null);
    try {
      const r = await axios.post(`${API}/clinician/credentialing/${caseDoc.id}/submit`, {}, getAuthHeader());
      toast.success(r.data.message || 'Submitted to payer');
      onChanged?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === 'object' && Array.isArray(detail.missing)) {
        setMissing(detail.missing);
        toast.error('Required items still incomplete', { description: `${detail.missing.length} item(s) need attention.` });
      } else {
        toast.error(detail || 'Submission failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const requiredItems = (caseDoc.checklist || []).filter((i) => i.required);
  const doneCount = requiredItems.filter((i) => i.done).length;
  const ready = doneCount === requiredItems.length;
  const isActive = caseDoc.status === 'active';

  return (
    <div
      className="cn-card border border-cn-border/70 bg-white dark:bg-cn-ink-800 overflow-hidden"
      data-testid={`credentialing-case-${caseDoc.id}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left p-5 hover:bg-cn-bg/40 transition-colors"
        data-testid={`credentialing-case-${caseDoc.id}-toggle`}
      >
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <Building2 className="w-4 h-4 text-cn-text-muted shrink-0" />
              <p className="font-display font-bold text-cn-text dark:text-white truncate">
                {caseDoc.payer_name}
              </p>
              <StagePill stage={caseDoc.stage} />
              {caseDoc.network && (
                <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-cn-bg/80 text-cn-text-muted">
                  {caseDoc.network}
                </span>
              )}
            </div>
            <p className="text-xs text-cn-text-muted mt-1">
              {doneCount}/{requiredItems.length} required docs · opened {fmtDate(caseDoc.created_at)}
              {caseDoc.tracking_id && <> · tracking {caseDoc.tracking_id}</>}
            </p>
            <div className="mt-3"><ProgressBar stage={caseDoc.stage} /></div>
          </div>
          <ChevronRight className={`w-5 h-5 text-cn-text-muted shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} />
        </div>
      </button>

      {open && (
        <div className="px-5 pb-5 border-t border-cn-border/60" data-testid={`credentialing-case-${caseDoc.id}-details`}>
          {/* CAQH attestation row */}
          {isActive && !['approved', 'denied'].includes(caseDoc.stage) && (
            <div className="mt-4 p-4 rounded-xl border border-cn-border/60 bg-cn-bg/40">
              <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">CAQH ProView</p>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <input
                  value={caqhId}
                  onChange={(e) => setCaqhId(e.target.value)}
                  placeholder="CAQH ProView ID"
                  className="cn-input flex-1 min-w-[160px]"
                  data-testid={`credentialing-case-${caseDoc.id}-caqh-input`}
                />
                <Button
                  type="button"
                  onClick={attestCaqh}
                  className="cn-btn cn-btn-primary !py-2 !px-3 !text-sm"
                  data-testid={`credentialing-case-${caseDoc.id}-caqh-attest`}
                >
                  Attest
                </Button>
                {caseDoc.caqh_attested_at && (
                  <span className="text-[11px] text-cn-success inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Attested {fmtDate(caseDoc.caqh_attested_at)}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Checklist */}
          <div className="mt-4">
            <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-2">
              Document checklist
            </p>
            <ul className="space-y-1.5">
              {(caseDoc.checklist || []).map((item) => (
                <li key={item.key} className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => isActive && toggleItem(item)}
                    disabled={!isActive}
                    className="shrink-0 disabled:cursor-not-allowed"
                    data-testid={`credentialing-checklist-${caseDoc.id}-${item.key}`}
                  >
                    {item.done
                      ? <CheckCircle2 className="w-4 h-4 text-cn-success" />
                      : <Circle className="w-4 h-4 text-cn-text-muted hover:text-cn-primary transition-colors" />}
                  </button>
                  <span className={`text-sm ${item.done ? 'text-cn-text-muted line-through' : 'text-cn-text dark:text-white'}`}>
                    {item.label}
                  </span>
                  {item.required && !item.done && (
                    <span className="text-[10px] uppercase tracking-wider font-bold text-cn-accent ml-auto">
                      Required
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Submit-to-payer row */}
          {isActive && !['payer_review', 'approved', 'denied'].includes(caseDoc.stage) && (
            <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs text-cn-text-muted">
                {ready
                  ? 'All required documents are in place — ready to submit to the payer.'
                  : `Complete ${requiredItems.length - doneCount} required item(s) to submit.`}
              </p>
              <Button
                type="button"
                disabled={submitting}
                onClick={submitToPayer}
                className={`cn-btn ${ready ? 'cn-btn-primary' : 'cn-btn-ghost'} !py-2 !px-3 !text-sm`}
                data-testid={`credentialing-case-${caseDoc.id}-submit`}
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                  <>Submit to payer <ArrowRight className="w-4 h-4 ml-1" /></>
                )}
              </Button>
            </div>
          )}

          {missing && missing.length > 0 && (
            <div className="mt-3 p-3 rounded-xl border border-cn-accent/40 bg-cn-accent/8" data-testid={`credentialing-case-${caseDoc.id}-missing`}>
              <p className="text-xs font-semibold text-cn-accent flex items-center gap-2">
                <AlertTriangle className="w-3.5 h-3.5" /> Required items still missing
              </p>
              <ul className="mt-2 ml-5 list-disc text-xs text-cn-text dark:text-white">
                {missing.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            </div>
          )}

          {/* Payer-review wait copy */}
          {caseDoc.stage === 'payer_review' && (
            <div className="mt-4 p-3 rounded-xl border border-cn-primary/30 bg-cn-primary/5 text-xs text-cn-text dark:text-white">
              <p className="font-semibold flex items-center gap-2 text-cn-primary">
                <Clock className="w-3.5 h-3.5" /> Payer review in progress
              </p>
              <p className="mt-1 text-cn-text-muted">
                Payer review typically takes 60–120 days. We&apos;ll notify you when there&apos;s a decision.
              </p>
            </div>
          )}

          {caseDoc.stage === 'approved' && (
            <div className="mt-4 p-3 rounded-xl border border-cn-success/40 bg-cn-success/8 text-xs">
              <p className="font-semibold text-cn-success flex items-center gap-2">
                <CheckCircle2 className="w-3.5 h-3.5" /> Approved · {fmtDate(caseDoc.effective_date)}
              </p>
              <p className="mt-1 text-cn-text-muted">
                You&apos;re a participating provider for {caseDoc.payer_name}.
              </p>
            </div>
          )}

          {caseDoc.stage === 'denied' && (
            <div className="mt-4 p-3 rounded-xl border border-cn-accent/40 bg-cn-accent/8 text-xs">
              <p className="font-semibold text-cn-accent flex items-center gap-2">
                <AlertTriangle className="w-3.5 h-3.5" /> Denied
              </p>
              <p className="mt-1 text-cn-text dark:text-white">{caseDoc.denial_reason || 'No reason recorded.'}</p>
            </div>
          )}

          {/* Activity feed */}
          {(caseDoc.events || []).length > 0 && (
            <div className="mt-5">
              <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-2">Activity</p>
              <ul className="space-y-1.5">
                {[...caseDoc.events].reverse().slice(0, 4).map((ev, i) => (
                  <li key={i} className="text-xs text-cn-text-muted">
                    <span className="text-cn-text dark:text-white">{ev.note}</span>
                    <span className="text-cn-text-muted"> · {fmtDate(ev.ts)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


// ───────────────────────────────────────────── Expirables editor ──────────
const ExpirablesEditor = ({ expirables, onSaved }) => {
  const { getAuthHeader } = useAuth();
  const [drafts, setDrafts] = useState(() => {
    // Pre-fill from existing data so each row remembers its number+date.
    const m = {};
    (expirables || []).forEach((e) => {
      m[e.key] = { number: e.number || '', expires_at: (e.expires_at || '').slice(0, 10) };
    });
    return m;
  });
  const [savingKey, setSavingKey] = useState(null);

  const save = async (key) => {
    const d = drafts[key] || {};
    if (!d.expires_at) { toast.error('Set an expiry date first'); return; }
    setSavingKey(key);
    try {
      await axios.post(`${API}/clinician/credentialing/expirables`,
        { key, number: d.number || null, expires_at: new Date(d.expires_at).toISOString() },
        getAuthHeader());
      toast.success(`${EXPIRABLE_LABEL[key]} saved`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSavingKey(null);
    }
  };

  const byKey = useMemo(() => {
    const m = {};
    (expirables || []).forEach((e) => { m[e.key] = e; });
    return m;
  }, [expirables]);

  return (
    <div className="cn-card p-5 border border-cn-border/70 bg-white dark:bg-cn-ink-800"
         data-testid="expirables-editor">
      <h3 className="font-display font-bold text-cn-text dark:text-white">Expiring credentials</h3>
      <p className="text-xs text-cn-text-muted mt-1">
        We&apos;ll surface alerts in your dashboard 90 days before each item expires so you can renew in time.
      </p>
      <div className="mt-4 space-y-3">
        {Object.entries(EXPIRABLE_LABEL).map(([key, label]) => {
          const stored = byKey[key];
          const draft = drafts[key] || { number: '', expires_at: '' };
          return (
            <div key={key} className="p-3 rounded-xl border border-cn-border/60 bg-cn-bg/40"
                 data-testid={`expirable-row-${key}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                <p className="text-sm font-semibold text-cn-text dark:text-white">{label}</p>
                {stored?.expired && (
                  <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-cn-accent/15 text-cn-accent">
                    Expired
                  </span>
                )}
                {stored && !stored.expired && stored.alert && (
                  <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-cn-accent/15 text-cn-accent">
                    Expires in {stored.days_until_expiry}d
                  </span>
                )}
                {stored && !stored.alert && (
                  <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-cn-success/15 text-cn-success">
                    Current
                  </span>
                )}
              </div>
              <div className="grid sm:grid-cols-[1fr_180px_auto] gap-2 items-end">
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">Number / ID</label>
                  <input
                    value={draft.number}
                    onChange={(e) => setDrafts((d) => ({ ...d, [key]: { ...draft, number: e.target.value } }))}
                    placeholder="optional"
                    className="cn-input w-full mt-1"
                    data-testid={`expirable-${key}-number`}
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">Expires</label>
                  <input
                    type="date"
                    value={draft.expires_at}
                    onChange={(e) => setDrafts((d) => ({ ...d, [key]: { ...draft, expires_at: e.target.value } }))}
                    className="cn-input w-full mt-1"
                    data-testid={`expirable-${key}-expires`}
                  />
                </div>
                <Button
                  type="button"
                  onClick={() => save(key)}
                  disabled={savingKey === key}
                  className="cn-btn cn-btn-primary !py-2 !px-3 !text-sm"
                  data-testid={`expirable-${key}-save`}
                >
                  {savingKey === key ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};


// ───────────────────────────────────────────── Page shell + body ──────────
export default function ClinicianCredentialing() {
  const { getAuthHeader } = useAuth();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showStart, setShowStart] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/clinician/credentialing`, getAuthHeader());
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not load credentialing.');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const summary = data?.summary || { payers_approved: 0, in_progress: 0, expiring_soon: 0, denied: 0 };
  const cases = data?.cases || [];
  const expirables = data?.expirables || [];

  const updateCaseInState = (updated) => {
    if (!updated) { load(); return; }
    setData((d) => d
      ? { ...d, cases: d.cases.map((c) => c.id === updated.id ? { ...c, ...updated } : c) }
      : d);
  };

  return (
    <DashboardShell
      navItems={clinicianNavItems({ pathname: location.pathname })}
      page={{ eyebrow: 'Practice', title: 'Credentialing' }}
    >
      <div className="space-y-6" data-testid="credentialing-page">
        {/* Page header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="cn-eyebrow inline-flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5" /> Payer enrollment
            </p>
            <h1 className="cn-display text-3xl sm:text-4xl mt-2">Your credentialing pipeline</h1>
            <p className="text-sm text-cn-text-muted mt-2 max-w-2xl">
              CaraLoom tracks every payer enrollment end-to-end — CAQH attestation, document
              packet, submission, and re-credentialing alerts. The payer makes the final call;
              we make sure nothing slips.
            </p>
          </div>
          <Button
            type="button"
            onClick={() => setShowStart((v) => !v)}
            className="cn-btn cn-btn-primary !py-2 !px-4 !text-sm inline-flex items-center gap-1.5"
            data-testid="credentialing-page-open-form"
          >
            <Plus className="w-4 h-4" /> New case
          </Button>
        </div>

        {/* KPI row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="cn-card p-4 border border-cn-success/30 bg-cn-success/8"
               data-testid="credentialing-kpi-approved">
            <p className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">Approved</p>
            <p className="text-3xl font-display font-extrabold text-cn-success mt-1">
              {summary.payers_approved}
            </p>
          </div>
          <div className="cn-card p-4 border border-cn-primary/30 bg-cn-primary/8"
               data-testid="credentialing-kpi-in-progress">
            <p className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">In progress</p>
            <p className="text-3xl font-display font-extrabold text-cn-primary mt-1">
              {summary.in_progress}
            </p>
          </div>
          <div className={`cn-card p-4 border ${summary.expiring_soon > 0
            ? 'border-cn-accent/40 bg-cn-accent/8' : 'border-cn-border/60 bg-cn-bg/40'}`}
               data-testid="credentialing-kpi-expiring">
            <p className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">Expiring &lt;90d</p>
            <p className={`text-3xl font-display font-extrabold mt-1 ${summary.expiring_soon > 0
              ? 'text-cn-accent' : 'text-cn-text-muted'}`}>
              {summary.expiring_soon}
            </p>
          </div>
          <div className="cn-card p-4 border border-cn-border/60 bg-cn-bg/40"
               data-testid="credentialing-kpi-denied">
            <p className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">Denied</p>
            <p className="text-3xl font-display font-extrabold text-cn-text-muted mt-1">
              {summary.denied}
            </p>
          </div>
        </div>

        {/* Start-case form (collapsed by default) */}
        {showStart && (
          <StartCaseForm
            onCreated={(c) => {
              setShowStart(false);
              setData((d) => d ? { ...d, cases: [c, ...d.cases] } : { cases: [c], summary, expirables: [] });
              load();
            }}
            onCancel={() => setShowStart(false)}
          />
        )}

        {/* Cases list */}
        <div className="space-y-3">
          {loading && (
            <div className="space-y-3">
              {[0, 1].map((i) => (
                <div key={i} className="h-24 rounded-2xl bg-cn-bg/60 border border-cn-border/60 animate-pulse" />
              ))}
            </div>
          )}
          {!loading && err && <p className="text-sm text-cn-text-muted">{err}</p>}
          {!loading && !err && cases.length === 0 && (
            <div className="cn-card p-6 border border-dashed border-cn-border/70 bg-cn-bg/60 text-center"
                 data-testid="credentialing-empty-state">
              <FileText className="w-6 h-6 text-cn-text-muted mx-auto mb-2" />
              <p className="text-sm font-semibold text-cn-text dark:text-white">No credentialing cases yet</p>
              <p className="text-xs text-cn-text-muted mt-1 max-w-md mx-auto">
                Open your first case to track CAQH, payer submission, and re-credentialing.
              </p>
              <Button
                type="button"
                onClick={() => setShowStart(true)}
                className="cn-btn cn-btn-primary !py-2 !px-3 !text-sm inline-flex items-center gap-1.5 mt-4"
                data-testid="credentialing-empty-cta"
              >
                <Plus className="w-4 h-4" /> Open first case
              </Button>
            </div>
          )}
          {!loading && !err && cases.map((c) => (
            <CaseCard key={c.id} caseDoc={c} onChanged={updateCaseInState} />
          ))}
        </div>

        {/* Expirables editor */}
        <ExpirablesEditor expirables={expirables} onSaved={load} />

        {/* Honest framing footer */}
        <div className="cn-card p-4 border border-cn-border/60 bg-cn-bg/40 flex items-start gap-3">
          <ShieldCheck className="w-4 h-4 text-cn-text-muted mt-0.5 shrink-0" />
          <p className="text-[11px] text-cn-text-muted">
            CaraLoom manages the credentialing workflow end-to-end. The final approval decision
            belongs to each payer (or their delegated CVO) — we cannot guarantee a yes. You can
            track payer status, internal notes, and re-credentialing dates here at any time.
            <a href="https://www.caqh.org/" target="_blank" rel="noopener noreferrer"
               className="ml-1 underline inline-flex items-center gap-1">
              CAQH ProView <ExternalLink className="w-3 h-3" />
            </a>
          </p>
        </div>
      </div>
    </DashboardShell>
  );
}
